/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2022. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { baseFontSize } from '../../utils/base-font-size.js';
import { WorklineFloatPositionerMixin } from './workline-float-positioner-mixin.js';

const renderDelay = 400;

/**
 * @prop {String} label - Defines the text shown on top of the workline.
 * @prop {Boolean} handleActive - If set to `true`, the active states of the items will be automatically handled upon clicking an item.
 * @attr {Boolean} handle-active - If set to true, the active states of the items will be automatically handled upon clicking an item.
 * @prop {Boolean} noScrollbar - If set to `true`, the scrollbar of the workline will be hidden. This property is especially for touch device applications.
 * @attr {Boolean} no-scrollbar - If set to true, the scrollbar of the workline will be hidden. This property is especially for touch device applications.
 * @slot - Slot where the main content is rendered. Takes workline items or workline groups.
 * @slot left - Contains information related to the whole scan process.
 * @slot right - Contains workflow controls such as buttons and guidance text.
 */
export class SHWorkline extends WorklineFloatPositionerMixin(LitElement) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      handleActive: { type: Boolean, reflect: true, attribute: 'handle-active' },
      noScrollbar: { type: Boolean, reflect: false, attribute: 'no-scrollbar' },
      /** @ignore */
      _onlyleftslot: { type: Boolean, reflect: true },
      /** @ignore */
      _onlyrightslot: { type: Boolean, reflect: true },
      /** @ignore */
      _withoutanyslots: { type: Boolean, reflect: true },
      /** @ignore */
      _emptyleft: { type: Boolean, reflect: true },
      /** @ignore */
      _emptyright: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: -webkit-box;
          display: flex;
          -webkit-box-orient: vertical;
          -webkit-box-direction: normal;
          flex-direction: column;
          overflow: visible;
          position: relative;
          --workline-wrapper-after-width: 100%;
          width: 100%;
        }
        .workline-wrapper {
          -webkit-box-orient: horizontal;
          -webkit-box-direction: normal;
          flex-direction: row;
          height: 7.375rem;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 0.75rem;
          width: 100%;
          padding-top: 0.5rem;
        }
        .workline-label {
          height: 2rem;
          position: relative;
        }
        :host(:not([label])) .workline-label {
          display: none;
        }
        .items-wrapper {
          -webkit-box-flex: 1;
          flex: 1;
          display: -webkit-box;
          display: flex;
          -webkit-box-pack: center;
          justify-content: center;
          height: 5rem;
          min-width: -webkit-fit-content;
          min-width: -moz-fit-content;
          min-width: fit-content;
          background: var(--base-2);
          align-items: center;
        }
        /* slots */
        .items-wrapper > ::slotted(sh-workline-item) {
          margin-left: 1rem;
        }
        .left-wrapper > ::slotted(*) {
          display: -webkit-box;
          margin-left: 0.5rem;
        }
        .right-wrapper > ::slotted(*) {
          margin-right: 1rem;
        }
        .right-wrapper {
          height: 5rem;
          padding-left: 1.5rem;
          display: -webkit-box;
          display: flex;
          -webkit-box-align: center;
          align-items: center;
          width: -webkit-fit-content;
          width: -moz-fit-content;
          width: fit-content;
          border-top-right-radius: 2.5rem;
          border-bottom-right-radius: 2.5rem;
          background: var(--base-2);
          position: relative;
          margin-top: 0.5rem;
        }
        .left-wrapper {
          width: -webkit-fit-content;
          width: -moz-fit-content;
          width: fit-content;
          display: -webkit-box;
          display: flex;
          border-top-left-radius: 2.5rem;
          border-bottom-left-radius: 2.5rem;
          background: var(--base-2);
          position: relative;
          height: 5rem;
          padding-right: 1.5rem;
          -webkit-box-align: center;
          align-items: center;
          margin-top: 0.5rem;
        }
        /* hide slots */
        :host([_emptyright]) .right-wrapper,
        :host([_emptyleft]) .left-wrapper {
          display: none;
        }
        .total-wrapper {
          display: -webkit-box;
          display: flex;
          height: -webkit-fit-content;
          height: -moz-fit-content;
          height: fit-content;
          width: 100%;
          -webkit-box-pack: justify;
          justify-content: space-between;
          position: relative;
        }
        .workline-wrapper.reset-alignment {
          top: -2.25rem;
        }
        :host([_withoutanyslots]) .items-wrapper {
          border-radius: 2.5rem;
        }
        :host([_onlyleftslot]) .items-wrapper {
          border-top-right-radius: 2.5rem;
          border-bottom-right-radius: 2.5rem;
        }
        :host([_onlyrightslot]) .items-wrapper {
          border-top-left-radius: 2.5rem;
          border-bottom-left-radius: 2.5rem;
        }
        .left-float,
        .right-float {
          position: absolute;
          height: 7.625rem;
          z-index: 1;
          margin-top: 0.5rem;
          display: flex;
        }
        .left-float > ::slotted(*),
        .right-float > ::slotted(*) {
          height: 5rem;
        }
        .left-float {
          left: 0rem;
        }
        .left-float > ::slotted(*) {
          margin-left: 1rem;
        }
        .right-float > ::slotted(*) {
          margin-right: 1rem;
        }
        .right-float {
          right: 0rem;
          flex-direction: row-reverse;
        }
        :host([no-scrollbar]) .workline-wrapper::-webkit-scrollbar {
          display: none;
        }
        :host([no-scrollbar]) .workline-wrapper {
          padding-bottom: 0rem;
        }
        .right-float {
          background-image: linear-gradient(
              to bottom,
              transparent,
              transparent 5rem,
              var(--base-1) 5rem,
              var(--base-1) 7.625rem
            ),
            linear-gradient(to right, transparent, transparent 3.5rem, var(--base-2) 3.5rem) !important;
        }
        .left-float {
          background-image: linear-gradient(
              to bottom,
              transparent,
              transparent 5rem,
              var(--base-1) 5rem,
              var(--base-1) 7.625rem
            ),
            linear-gradient(to left, transparent 3.5rem, var(--base-2) 3.5rem) !important;
        }

        .items-wrapper > ::slotted(*.stuck-right),
        .items-wrapper > ::slotted(*.stuck-left) {
          background-color: rgba(var(--ui-5), var(--opacity-1));
          box-shadow: var(--shadow-overlay);
        }
        .items-wrapper > ::slotted(*.stuck-right[active]),
        .items-wrapper > ::slotted(*.stuck-left[active]) {
          box-shadow: var(--shadow-overlay),
            inset 0rem 0rem 0rem 0.0625rem rgba(var(--ui-1), var(--opacity-1));
        }
      `,
    ];
  }
  render() {
    return html`
      <sh-text size="super-header" class="workline-label" style="line-height: 2rem"
        >${this.label}</sh-text
      >
      <div class="total-wrapper">
        <div class="left-float"></div>
        <div class="left-wrapper">
          <slot
            name="left"
            id="left"
            @slotchange="${(e) => this._handleLeftSlotChanged(e.target)}"
          ></slot>
        </div>
        <div class="workline-wrapper" @scroll="${() => this._handleScroll()}">
          <div class="items-wrapper">
            <slot id="body" @slotchange="${() => this._handleBodySlotChanged()}"></slot>
          </div>
        </div>
        <div class="right-wrapper">
          <slot
            name="right"
            id="right"
            @slotchange="${(e) => this._handleRightSlotChanged(e.target)}"
          ></slot>
        </div>
        <div class="right-float"></div>
      </div>
    `;
  }

  constructor() {
    super();
    this.handleActive = false;
    this._emptyleft = true;
    this._emptyright = true;
    this._onlyleftslot = false;
    this._onlyrightslot = false;
    this._withoutanyslots = true;
  }
  firstUpdated() {
    super.firstUpdated();
  }
  connectedCallback() {
    super.connectedCallback();
    this.windowDimensionObserver = this._handleSizeFloatChange.bind(this);
    window.addEventListener('resize', this.windowDimensionObserver);

    this._worklineitemfloatchangedListener = this._handleFloatChange.bind(this);
    this._worklineitemsizechangedListener = this._handleSizeFloatChange.bind(this);
    this._worklineclickListener = this._handleActive.bind(this);
    this.addEventListener('click', this._worklineclickListener);
    this.addEventListener('workline-item-float-changed', this._worklineitemfloatchangedListener);
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.windowDimensionObserver);
    this.removeEventListener('workline-item-float-changed', this._worklineitemfloatchangedListener);
    this.removeEventListener('workline-item-size-changed', this._worklineitemsizechangedListener);
    this.removeEventListener('click', this._worklineclickListener);
    super.disconnectedCallback();
  }
  // float change handler which adds space for smooth effect
  _handleFloatChange() {
    const floatElements = this.querySelectorAll('sh-workline-item[float]');
    this._addEmptySpace(floatElements);
  }
  _handleSizeFloatChange() {
    const floatElements = this.querySelectorAll('sh-workline-item[float]');
    if (floatElements.length == 0) {
      return;
    }
    this.calculateFloatPositions();
    this.fixFloatItems();
  }
  _addEmptySpace(floatElements) {
    floatElements.forEach((element) => {
      if (element.previousElementSibling) {
        if (element.previousElementSibling.tagName !== 'DIV') {
          let emptySpace = document.createElement('div');
          emptySpace.style.minWidth = '0rem';
          element.parentNode.insertBefore(emptySpace, element);
        }
      } else {
        let emptySpace = document.createElement('div');
        emptySpace.style.minWidth = '0rem';
        element.parentNode.insertBefore(emptySpace, element);
      }
    });
  }
  _handleLeftSlotChanged(slot) {
    const numberOfElementsInLeftSlot = slot.assignedNodes({
      flatten: true,
    }).length;
    if (numberOfElementsInLeftSlot > 0) {
      this._emptyleft = false;
      this._onlyleftslot = true;
      this._withoutanyslots = false;
    }
    if (this._onlyrightslot && this._onlyleftslot) {
      this._onlyrightslot = false;
      this._onlyleftslot = false;
    }
    this.leftSlotDelay = setTimeout(() => {
      clearTimeout(this.leftSlotDelay);
      this.shadowRoot.querySelector('.left-float').style.left = `${
        this.shadowRoot.querySelector('.left-wrapper').getBoundingClientRect().width / baseFontSize
      }rem`;
      this.calculateFloatPositions();
      this.fixFloatItems();
    }, renderDelay);
  }
  _handleRightSlotChanged(slot) {
    const numberOfElementsInrightSlot = slot.assignedNodes({
      flatten: true,
    }).length;
    if (numberOfElementsInrightSlot > 0) {
      this._emptyright = false;
      this._onlyrightslot = true;
      this._withoutanyslots = false;
    }
    if (this._onlyrightslot && this._onlyleftslot) {
      this._onlyrightslot = false;
      this._onlyleftslot = false;
    }
    this.rightSlotDelay = setTimeout(() => {
      clearTimeout(this.rightSlotDelay);
      this.shadowRoot.querySelector('.right-float').style.right = `${
        this.shadowRoot.querySelector('.right-wrapper').getBoundingClientRect().width / baseFontSize
      }rem`;
      this.calculateFloatPositions();
      this.fixFloatItems();
    }, renderDelay);
  }
  _handleBodySlotChanged() {
    this._handleSizeFloatChange();
    const floatElements = this.querySelectorAll('sh-workline-item[float]');
    this._addEmptySpace(floatElements);

    this.addEventListener('workline-item-size-changed', this._worklineitemsizechangedListener);
  }
  _handleScroll() {
    const floatElements = this.querySelectorAll('sh-workline-item[float]');
    if (floatElements.length === 0) {
      return;
    }
    this.fixFloatItems();
  }
  _handleActive(event) {
    const BodyChilds = this.shadowRoot.querySelector('#body').assignedNodes({
      flatten: true,
    });

    let allChildren = [];

    for (let index = 0; index < BodyChilds.length; index++) {
      if (BodyChilds[index].tagName == 'SH-WORKLINE-GROUP') {
        let worklineGroup = BodyChilds[index].childNodes;
        for (let index1 = 0; index1 < worklineGroup.length; index1++) {
          if (worklineGroup[index1].tagName == 'SH-WORKLINE-ITEM') {
            allChildren.push(worklineGroup[index1]);
          }
        }
      } else if (BodyChilds[index].tagName == 'SH-WORKLINE-ITEM') {
        allChildren.push(BodyChilds[index]);
      }
    }
    // remove active attr from all other items
    if (event.target.tagName === 'SH-WORKLINE-ITEM' && this.handleActive === true) {
      for (let i = 0; i < allChildren.length; i++) {
        if (allChildren[i].hasAttribute('active')) {
          allChildren[i].active = false;
        }
      }
      event.target.active = true;
    }
  }
}
customElements.define('sh-workline', SHWorkline);
